import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import Select from "react-select";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ---------- Helpers ----------
const pad = (n) => String(n).padStart(2, "0");
const getTodayYMD = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const getNowHHMM = () => {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function formatTime12(time24) {
  if (!time24) return "";
  if (time24 instanceof Date) {
    let h = time24.getHours();
    const m = pad(time24.getMinutes());
    h = h % 12 || 12;
    const ampm = time24.getHours() >= 12 ? "PM" : "AM";
    return `${pad(h)}:${m} ${ampm}`;
  }
  const t = String(time24).trim();
  const m = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return t;
  let hh = Number(m[1]);
  const mm = pad(Number(m[2]));
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12 || 12;
  return `${pad(hh)}:${mm} ${ampm}`;
}

function parseServerDateToLocal(s) {
  if (!s) return null;
  if (s instanceof Date) return s;
  const normalized = String(s).trim().replace(" ", "T");
  const m = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (!m) {
    const fallback = new Date(s);
    return isNaN(fallback) ? null : fallback;
  }
  const year = Number(m[1]),
    month = Number(m[2]) - 1,
    day = Number(m[3]);
  const hour = Number(m[4] ?? 0),
    minute = Number(m[5] ?? 0),
    second = Number(m[6] ?? 0);
  return new Date(year, month, day, hour, minute, second);
}

function formatDateTime12(s) {
  const dt = parseServerDateToLocal(s);
  if (!dt) return "";
  const day = pad(dt.getDate());
  const months = [
    "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
  ];
  const mon = months[dt.getMonth()];
  const year = dt.getFullYear();
  let hr = dt.getHours();
  const min = pad(dt.getMinutes());
  hr = hr % 12 || 12;
  const ampm = dt.getHours() >= 12 ? "PM" : "AM";
  return `${day}-${mon}-${year} ${pad(hr)}:${min} ${ampm}`;
}

// ---------- Component ----------
export default function IpdReports() {
  const [activeTab, setActiveTab] = useState("discharged");
  const [filters, setFilters] = useState({
    dischargeFrom: getTodayYMD(),
    dischargeFromTime: "00:00",
    dischargeTo: getTodayYMD(),
    dischargeToTime: getNowHHMM(),
    doctorId: "",
    doctorSearch: "",
    search: "",
    status: "1"
  });

  const [records, setRecords] = useState([]);
  const [docsFromServer, setDocsFromServer] = useState([]);
  const [totals, setTotals] = useState({
    totalIpdBill: "0.00",
    totalAdvance: "0.00",
    paidCash: "0.00",
    paidCard: "0.00",
    paidOnline: "0.00",
    totalDue: "0.00",
    totalPatients: 0,
    doctorRoleTotal: "0.00",
    doctorSurgeon: "0.00",
    doctorAsst: "0.00",
    doctorAnes: "0.00",
    doctorPedia: "0.00"
  });

  // Doctor Payment Modal State
  const [isDoctorPayModalOpen, setIsDoctorPayModalOpen] = useState(false);
  const [doctorPayData, setDoctorPayData] = useState({
    doctorId: "",
    doctorName: "",
    patients: [],
    totalAmount: 0,
    payAmount: 0,
    paymentMethod: "Cash",
    loading: false
  });

  // Payment status tracking for filtered records
  const [paymentStatuses, setPaymentStatuses] = useState({});

  // Helper to get doctor name by ID
  const getDoctorNameById = (doctorId) => {
    if (!doctorId) return "";
    const doc = docsFromServer.find(d =>
      String(d.user_id) === String(doctorId) ||
      String(d.id) === String(doctorId)
    );
    return doc ? (doc.fname || doc.name || doc.username || '') : '';
  };

  // Helper to check if a doctor value matches the selected filter
  const doctorMatchesFilter = (doctorValue) => {
    if (!filters.doctorId || filters.doctorId === "") return true;
    if (!doctorValue) return false;

    const selectedId = String(filters.doctorId).toLowerCase();
    const docValue = String(doctorValue).toLowerCase();

    if (docValue === selectedId) return true;

    const selectedDoc = docsFromServer.find(d =>
      String(d.user_id) === String(filters.doctorId) || String(d.id) === String(filters.doctorId)
    );

    if (selectedDoc) {
      const selectedNames = [
        selectedDoc.fname,
        selectedDoc.name,
        selectedDoc.username
      ].filter(Boolean).map(x => String(x).toLowerCase());

      for (const name of selectedNames) {
        if (docValue === name || docValue.includes(name)) return true;
      }
    }

    return false;
  };

  // Fetch doctors
  const fetchDoctors = async () => {
    try {
      const fd = new FormData();
      fd.append("type", "doctor");
      fd.append("page_no", "none");
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd);
      if (res?.data?.status && Array.isArray(res.data.records)) {
        setDocsFromServer(res.data.records);
      }
    } catch (e) {
      console.error("fetchDoctors error:", e);
    }
  };

  // Load records from server
  const loadRecords = async () => {
    try {
      const fd = new FormData();
      fd.append("page_no", "none");

      if (filters.dischargeFrom) fd.append("dischargeFrom", filters.dischargeFrom);
      if (filters.dischargeFromTime) fd.append("dischargeFromTime", filters.dischargeFromTime);
      if (filters.dischargeTo) fd.append("dischargeTo", filters.dischargeTo);
      if (filters.dischargeToTime) fd.append("dischargeToTime", filters.dischargeToTime);

      if (typeof filters.status !== "undefined" && filters.status !== "") fd.append("status", String(filters.status));
      if (filters.search) fd.append("search_key", filters.search);
      if (filters.doctorId) fd.append("doctor_id", filters.doctorId);

      const url = AppConst.BASE_URL + (AppConst.getIpdDischargedUrl3);
      const res = await axios.post(url, fd);

      if (res?.data?.status && Array.isArray(res.data.records)) {
        setRecords(res.data.records);
        const t = res.data.totals || {};
        setTotals(prev => ({
          ...prev,
          totalIpdBill: (t.sum_ipd_bill ?? 0).toFixed(2),
          paidCash: (t.total_cash ?? 0).toFixed(2),
          paidCard: (t.total_card ?? 0).toFixed(2),
          paidOnline: (t.total_online ?? 0).toFixed(2),
          totalDue: (t.sum_due ?? 0).toFixed(2),
          totalPatients: (t.count ?? (res.data.records || []).length)
        }));

        // If doctor is filtered, fetch payment statuses
        if (filters.doctorId) {
          fetchPaymentStatuses(res.data.records);
        }
      } else {
        setRecords([]);
        setTotals({
          totalIpdBill: "0.00", totalAdvance: "0.00", paidCash: "0.00",
          paidCard: "0.00", paidOnline: "0.00", totalDue: "0.00", totalPatients: 0,
          doctorRoleTotal: "0.00", doctorSurgeon: "0.00", doctorAsst: "0.00", doctorAnes: "0.00", doctorPedia: "0.00"
        });
      }
    } catch (e) {
      console.error("loadRecords error:", e);
      setRecords([]);
    }
  };

  // Fetch payment statuses for filtered doctor
  const fetchPaymentStatuses = async (recs) => {
    if (!filters.doctorId) return;
  
    try {
      const ipdIds = recs.map(r => r.ipd_id).filter(Boolean);
      if (ipdIds.length === 0) return;
  
      const fd = new FormData();
      fd.append("doctor_id", filters.doctorId);
      ipdIds.forEach(id => fd.append("ipd_ids[]", id));
  
      // Fix: Use correct URL format
      const res = await axios.post(
        AppConst.BASE_URL + "Ipdcon/get_doctor_service_payment_status",
        fd
      );
  
      if (res?.data?.status && res.data.payments) {
        setPaymentStatuses(res.data.payments);
      } else {
        setPaymentStatuses({});
      }
    } catch (e) {
      console.error("fetchPaymentStatuses error:", e);
      setPaymentStatuses({});
    }
  };

  useEffect(() => {
    loadRecords();
    fetchDoctors();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadRecords(), 220);
    return () => clearTimeout(t);
  }, [filters]);

  // Filtering
  const filteredRecords = useMemo(() => {
    let data = [...records];
    data = data.filter(r => String(r.status) === String(filters.status));

    if (filters.doctorId && filters.doctorId !== "") {
      data = data.filter(r => {
        const referredDoctorId = String(r.ipd_bill_refered_doctor ?? r.ipd_refer_doctor ?? r.ipd_ref_doc ?? r.ipd_cons_doc ?? '');
        const referredDoctorName = (r.ipd_bill_refered_doctor_name || r.ipd_refer_doctor_name || r.ipd_ref_doc_name || r.ipd_cons_doc_name || r.consultant_name || '');

        if (doctorMatchesFilter(referredDoctorId) || doctorMatchesFilter(referredDoctorName)) return true;

        const otSurgeon = r.ot_doctor || r.ot_surgeon || r.ot_surgeon_name || r.surgeon_name || '';
        if (doctorMatchesFilter(otSurgeon)) return true;

        const otAsst = r.ot_asst_doctor || r.ot_assistant || r.asst_surgeon || r.assistant_surgeon || '';
        if (doctorMatchesFilter(otAsst)) return true;

        const otAnesthesia = r.ot_anesthesia_doctor || r.ot_anesthesia || r.anesthesia_doctor || '';
        if (doctorMatchesFilter(otAnesthesia)) return true;

        const otPediatric = r.ot_pediatric_doctor || r.ot_paediatric_doctor || r.pediatric_doctor || '';
        if (doctorMatchesFilter(otPediatric)) return true;

        return false;
      });
    }

    if (filters.search && filters.search.trim() !== "") {
      const skRaw = filters.search.trim();
      const sk = skRaw.toLowerCase();
      const digitsOnly = skRaw.replace(/\D/g, '');

      data = data.filter(r => {
        const uhid = ((r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '').toString().toLowerCase();
        const name = (r.ipd_patient_name || '').toString().toLowerCase();
        const phones = [
          r.ipd_patient_mobile,
          r.ipd_cont_number,
          r.ipd_patient_phone,
          r.patient_mobile,
          r.mobile,
          r.phone
        ].filter(Boolean).map(p => String(p).toLowerCase());
        const ipdNoDisplayed = `000${r.ipd_id ?? r.id ?? ''}`.toLowerCase();

        if (name.includes(sk)) return true;
        if (uhid.includes(sk)) return true;
        if (ipdNoDisplayed.includes(sk)) return true;
        for (const ph of phones) {
          if (ph.includes(sk)) return true;
          if (digitsOnly && ph.replace(/\D/g, '').includes(digitsOnly)) return true;
        }
        if (digitsOnly) {
          const ipdIdStr = String(r.ipd_id ?? r.id ?? '').replace(/\D/g, '');
          if (ipdIdStr.includes(digitsOnly)) return true;
        }
        return false;
      });
    }

    if (filters.dischargeFrom || filters.dischargeTo) {
      const fromDt = filters.dischargeFrom ? new Date(`${filters.dischargeFrom}T${filters.dischargeFromTime || '00:00'}`) : null;
      const toDt = filters.dischargeTo ? new Date(`${filters.dischargeTo}T${filters.dischargeToTime || '23:59'}`) : null;
      data = data.filter(r => {
        const ds = r.ipd_discharge_date || r.discharge_datetime || r.discharge_date || null;
        const dt = parseServerDateToLocal(ds);
        if (!dt) return false;
        if (fromDt && dt < fromDt) return false;
        if (toDt && dt > toDt) return false;
        return true;
      });
    }

    return data;
  }, [records, filters, docsFromServer]);

  // Compute totals from filtered list
  useEffect(() => {
    const num = (v) => { const n = Number(v ?? 0); return isNaN(n) ? 0 : n; };
    let totalIpd = 0, totalAdvance = 0, paidCash = 0, paidCard = 0, paidOnline = 0, totalDue = 0;
    let doctorSurgeon = 0, doctorAsst = 0, doctorAnes = 0, doctorPedia = 0;

    filteredRecords.forEach(r => {
      const advance = num(r.ipd_advance ?? r.advance ?? r.advance_amt ?? 0);
      const ipdBill = num(r.ipd_bill_total_final ?? r.ipd_ipd_bill_amount ?? r.ipd_bill_amount ?? r.ipd_final_amt ?? 0);
      const due = num(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0);

      totalAdvance += advance;
      totalIpd += ipdBill;
      totalDue += due;

      paidCash += num(r.ipd_paid_cash ?? r.paid_cash ?? r.paidCash ?? 0);
      paidCard += num(r.ipd_paid_card ?? r.paid_card ?? r.paidCard ?? 0);
      paidOnline += num(r.ipd_paid_online ?? r.paid_online ?? r.paidOnline ?? 0);

      doctorSurgeon += num(r.ot_surgeon_amount ?? r.surgeon_amount ?? 0);
      doctorAsst += num(r.ot_asst_amount ?? r.asst_amount ?? 0);
      doctorAnes += num(r.ot_anesthesia_amount ?? r.anesthesia_amount ?? 0);
      doctorPedia += num(r.ot_pediatric_amount ?? r.pediatric_amount ?? 0);
    });

    const doctorRoleTotal = doctorSurgeon + doctorAsst + doctorAnes + doctorPedia;

    setTotals(prev => ({
      ...prev,
      totalIpdBill: totalIpd.toFixed(2),
      totalAdvance: totalAdvance.toFixed(2),
      paidCash: paidCash.toFixed(2),
      paidCard: paidCard.toFixed(2),
      paidOnline: paidOnline.toFixed(2),
      totalDue: totalDue.toFixed(2),
      totalPatients: filteredRecords.length,
      doctorRoleTotal: doctorRoleTotal.toFixed(2),
      doctorSurgeon: doctorSurgeon.toFixed(2),
      doctorAsst: doctorAsst.toFixed(2),
      doctorAnes: doctorAnes.toFixed(2),
      doctorPedia: doctorPedia.toFixed(2)
    }));
  }, [filteredRecords]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Get payment status badge for a record
  const getPaymentStatusBadge = (record, role) => {
    const key = `${record.ipd_id}_${role}`;
    const status = paymentStatuses[key];

    if (!status) {
      return <span className="badge bg-danger">Not Paid</span>;
    }

    if (status.status === 'paid') {
      return <span className="badge bg-success">Paid</span>;
    } else if (status.status === 'partial') {
      return (
        <span className="badge bg-warning text-dark">
          Partial (Due: ₹{status.due.toFixed(2)})
        </span>
      );
    }
    return <span className="badge bg-danger">Not Paid</span>;
  };

  // Open Doctor Pay Modal
  const openDoctorPayModal = () => {
    if (!filters.doctorId) {
      Swal.fire('No Doctor Selected', 'Please filter by a doctor first.', 'info');
      return;
    }

    // Build patient list with their role amounts
    const patients = [];
    const num = (v) => { const n = Number(v ?? 0); return isNaN(n) ? 0 : n; };

    filteredRecords.forEach(r => {
      const otSurgeon = r.ot_doctor || r.ot_surgeon || '';
      const otAsst = r.ot_asst_doctor || r.asst_surgeon || '';
      const otAnes = r.ot_anesthesia_doctor || r.anesthesia_doctor || '';
      const otPedia = r.ot_pediatric_doctor || r.pediatric_doctor || '';

      // Check which role this doctor has for this patient
      if (doctorMatchesFilter(otSurgeon) && num(r.ot_surgeon_amount) > 0) {
        const key = `${r.ipd_id}_surgeon`;
        const existingPayment = paymentStatuses[key];
        const paidAlready = existingPayment?.paid || 0;
        const roleAmount = num(r.ot_surgeon_amount);
        const due = roleAmount - paidAlready;

        if (due > 0) {
          patients.push({
            ipd_id: r.ipd_id,
            bill_key: r.ipd_bill_id || r.ipd_uhid || r.ipd_no || r.ipd_id,
            patient_name: r.ipd_patient_name || '',
            uhid: (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '',
            role: 'surgeon',
            role_label: 'Surgeon',
            role_amount: roleAmount,
            paid: paidAlready,
            due: due
          });
        }
      }

      if (doctorMatchesFilter(otAsst) && num(r.ot_asst_amount) > 0) {
        const key = `${r.ipd_id}_asst`;
        const existingPayment = paymentStatuses[key];
        const paidAlready = existingPayment?.paid || 0;
        const roleAmount = num(r.ot_asst_amount);
        const due = roleAmount - paidAlready;

        if (due > 0) {
          patients.push({
            ipd_id: r.ipd_id,
            bill_key: r.ipd_bill_id || r.ipd_uhid || r.ipd_no || r.ipd_id,
            patient_name: r.ipd_patient_name || '',
            uhid: (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '',
            role: 'asst',
            role_label: 'Assistant',
            role_amount: roleAmount,
            paid: paidAlready,
            due: due
          });
        }
      }

      if (doctorMatchesFilter(otAnes) && num(r.ot_anesthesia_amount) > 0) {
        const key = `${r.ipd_id}_anesthesia`;
        const existingPayment = paymentStatuses[key];
        const paidAlready = existingPayment?.paid || 0;
        const roleAmount = num(r.ot_anesthesia_amount);
        const due = roleAmount - paidAlready;

        if (due > 0) {
          patients.push({
            ipd_id: r.ipd_id,
            bill_key: r.ipd_bill_id || r.ipd_uhid || r.ipd_no || r.ipd_id,
            patient_name: r.ipd_patient_name || '',
            uhid: (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '',
            role: 'anesthesia',
            role_label: 'Anesthesia',
            role_amount: roleAmount,
            paid: paidAlready,
            due: due
          });
        }
      }

      if (doctorMatchesFilter(otPedia) && num(r.ot_pediatric_amount) > 0) {
        const key = `${r.ipd_id}_pediatric`;
        const existingPayment = paymentStatuses[key];
        const paidAlready = existingPayment?.paid || 0;
        const roleAmount = num(r.ot_pediatric_amount);
        const due = roleAmount - paidAlready;

        if (due > 0) {
          patients.push({
            ipd_id: r.ipd_id,
            bill_key: r.ipd_bill_id || r.ipd_uhid || r.ipd_no || r.ipd_id,
            patient_name: r.ipd_patient_name || '',
            uhid: (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '',
            role: 'pediatric',
            role_label: 'Pediatric',
            role_amount: roleAmount,
            paid: paidAlready,
            due: due
          });
        }
      }
    });

    const totalDue = patients.reduce((s, p) => s + p.due, 0);

    if (patients.length === 0 || totalDue <= 0) {
      Swal.fire('Nothing to Pay', 'All services for this doctor are already paid.', 'info');
      return;
    }

    setDoctorPayData({
      doctorId: filters.doctorId,
      doctorName: filters.doctorSearch || getDoctorNameById(filters.doctorId) || filters.doctorId,
      patients: patients,
      totalAmount: totalDue,
      payAmount: totalDue,
      paymentMethod: "Cash",
      loading: false
    });

    setIsDoctorPayModalOpen(true);
  };

  // Close Doctor Pay Modal
  const closeDoctorPayModal = () => {
    setIsDoctorPayModalOpen(false);
    setDoctorPayData({
      doctorId: "",
      doctorName: "",
      patients: [],
      totalAmount: 0,
      payAmount: 0,
      paymentMethod: "Cash",
      loading: false
    });
  };

  // Submit Doctor Payment
  const submitDoctorPayment = async () => {
    const payAmt = Number(doctorPayData.payAmount);
    if (isNaN(payAmt) || payAmt <= 0) {
      Swal.fire('Invalid Amount', 'Please enter a valid payment amount.', 'warning');
      return;
    }
  
    setDoctorPayData(prev => ({ ...prev, loading: true }));
  
    try {
      // Use FormData for better compatibility
      const fd = new FormData();
      fd.append('doctor_id', doctorPayData.doctorId);
      fd.append('doctor_name', doctorPayData.doctorName);
      fd.append('pay_amount', payAmt);
      fd.append('payment_method', doctorPayData.paymentMethod);
      
      // Send patients as JSON string
      const patientsData = doctorPayData.patients.map(p => ({
        ipd_id: p.ipd_id,
        bill_key: p.bill_key,
        patient_name: p.patient_name,
        role: p.role,
        role_amount: p.due,
        due: p.due
      }));
      fd.append('patients', JSON.stringify(patientsData));
  
      // Make the API call
      const res = await axios.post(
        AppConst.BASE_URL + "Ipdcon/pay_doctor_services",
        fd
      );
  
      console.log('Payment response:', res.data);
  
      if (res?.data?.status) {
        Swal.fire('Payment Successful', `Total Paid: ₹${Number(res.data.total_paid || 0).toFixed(2)}`, 'success');
        
        // Refresh data
        closeDoctorPayModal();
        loadRecords();
      } else {
        throw new Error(res?.data?.error || 'Payment failed');
      }
    } catch (e) {
      console.error('submitDoctorPayment error:', e);
      Swal.fire('Payment Failed', e?.response?.data?.error || e?.message || 'Server error', 'error');
    } finally {
      setDoctorPayData(prev => ({ ...prev, loading: false }));
    }
  };
  
  // ============================================
  // ALTERNATIVE: If you prefer JSON format, use this version
  // ============================================
  const submitDoctorPaymentJSON = async () => {
    const payAmt = Number(doctorPayData.payAmount);
    if (isNaN(payAmt) || payAmt <= 0) {
      Swal.fire('Invalid Amount', 'Please enter a valid payment amount.', 'warning');
      return;
    }
  
    setDoctorPayData(prev => ({ ...prev, loading: true }));
  
    try {
      const payload = {
        doctor_id: doctorPayData.doctorId,
        doctor_name: doctorPayData.doctorName,
        pay_amount: payAmt,
        payment_method: doctorPayData.paymentMethod,
        patients: doctorPayData.patients.map(p => ({
          ipd_id: p.ipd_id,
          bill_key: p.bill_key,
          patient_name: p.patient_name,
          role: p.role,
          role_amount: p.due,
          due: p.due
        }))
      };
  
      // Send as JSON with proper headers
      const res = await axios.post(
        AppConst.BASE_URL + "Ipdcon/pay_doctor_services",
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
  
      console.log('Payment response:', res.data);
  
      if (res?.data?.status) {
        Swal.fire('Payment Successful', `Total Paid: ₹${Number(res.data.total_paid || 0).toFixed(2)}`, 'success');
        closeDoctorPayModal();
        loadRecords();
      } else {
        throw new Error(res?.data?.error || 'Payment failed');
      }
    } catch (e) {
      console.error('submitDoctorPayment error:', e);
      Swal.fire('Payment Failed', e?.response?.data?.error || e?.message || 'Server error', 'error');
    } finally {
      setDoctorPayData(prev => ({ ...prev, loading: false }));
    }
  };

  // Calculate preview of payment distribution
  const getPaymentPreview = () => {
    const payAmt = Number(doctorPayData.payAmount);
    if (isNaN(payAmt) || payAmt <= 0) return [];

    let remaining = payAmt;
    const preview = [];

    for (const p of doctorPayData.patients) {
      if (remaining <= 0) {
        preview.push({ ...p, willPay: 0, newStatus: 'not_paid' });
      } else {
        const willPay = Math.min(remaining, p.due);
        const newDue = p.due - willPay;
        let newStatus = 'not_paid';

        if (newDue <= 0) {
          newStatus = 'paid';
        } else if (willPay > 0) {
          newStatus = 'partial';
        }

        preview.push({ ...p, willPay, newDue, newStatus });
        remaining -= willPay;
      }
    }

    return preview;
  };

  const esc = (s) => {
    if (s === null || typeof s === 'undefined') return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
  };

  const openPatientCard = async (row) => {
    if (!row) return;

    const uhid = row.ipd_uhid || row.opd_uhid_move || row.opd_uhid || '';
    const wardName = row.ipd_ward_name || row.ward_name || row.ipd_ward || 'N/A';
    const bedName  = row.ipd_bed_name  || row.bed_name || row.ipd_bed || 'N/A';
    const referred = (row.ipd_bill_refered_doctor_name || row.ipd_refer_doctor_name || row.ipd_ref_doc_name || row.ipd_cons_doc_name || row.consultant_name) || '';
    const regDate = formatDateTime12(row.ipd_datetime);
    const discharge = formatDateTime12(row.discharge_datetime);

    const ipdBillFinal = Number(row.ipd_bill_total_final ?? row.ipd_bill_final_amt ?? row.ipd_final_amt ?? 0).toFixed(2);
    const ipdDue = Number(row.ipd_bill_total_due ?? row.ipd_due ?? row.due ?? 0).toFixed(2);

    const patientHtml = `
      <div style="text-align:left">
        <p><strong>UHID:</strong> ${esc(uhid) || 'N/A'}</p>
        <p><strong>IPD No:</strong> ${esc(row.ipd_id ?? row.id ?? 'N/A')}</p>
        <p><strong>Name:</strong> ${esc(row.ipd_patient_name ?? 'N/A')}</p>
        <p><strong>Phone:</strong> ${esc(row.ipd_patient_mobile ?? row.ipd_cont_number ?? 'N/A')}</p>
        <p><strong>Ward:</strong> ${esc(wardName)} &nbsp; <strong>Bed:</strong> ${esc(bedName)}</p>
        <p><strong>Admission:</strong> ${esc(regDate)}</p>
        <p><strong>Discharge:</strong> ${esc(discharge)}</p>
        <p><strong>Referred Doctor:</strong> ${esc(referred || '-')}</p>
        <p><strong>Reg Charges:</strong> ${Number(row.ipd_reg_charges ?? 0).toFixed(2)}</p>
        <p><strong>Advance:</strong> ${Number(row.ipd_advance ?? 0).toFixed(2)}</p>
        <p><strong>IPD Bill Final:</strong> ${ipdBillFinal} &nbsp; <strong>Due:</strong> ${ipdDue}</p>
      </div>
    `;

    Swal.fire({
      title: `Patient: ${esc(row.ipd_patient_name ?? '')}`,
      html: patientHtml,
      width: 700,
      showCloseButton: true,
      confirmButtonText: 'Close'
    });
  };

  const viewButtonColumn = {
    name: 'View',
    width: '80px',
    cell: (r) => (
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={(e) => {
          e.stopPropagation();
          openPatientCard(r);
        }}
      >
        View
      </button>
    )
  };

  const commonColumns = [
    { name: 'SL', cell: (r, i) => i + 1, width: '50px' },
    { name: 'IPD No', selector: r => `000${r.ipd_id ?? r.id ?? ''}`, sortable: true, width: '100px' },
    { name: 'UHID', selector: r => (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '', sortable: true, width: '120px' },
    { name: 'Patient Name', selector: r => `${r.ipd_patient_title || ''} ${r.ipd_patient_name || ''}`, cell: r => (<div>{r.ipd_patient_name}<br/><small className="text-muted">{r.ipd_patient_mobile || r.ipd_cont_number}</small></div>), wrap: true, width: '180px' },
    { name: 'Bed', selector: r => r.ipd_bed_name || r.ipd_bed || '', sortable: true, width: '100px' }
  ];

  const dischargedColumns = [
    ...commonColumns.slice(0,3),
    { name: 'Discharge Date', selector: r => r.ipd_discharge_date || r.discharge_datetime || '', sortable: true, cell: r => formatDateTime12(r.ipd_discharge_date || r.discharge_datetime), width: '160px' },
    ...commonColumns.slice(3),

    // OT Surgeon with payment status
    {
      name: 'OT Surgeon',
      width: '200px',
      cell: (r) => {
        let doctorName = r.ot_doctor || r.ot_surgeon || r.surgeon_name || '';
        if (doctorName && !isNaN(doctorName)) doctorName = getDoctorNameById(doctorName) || doctorName;
        const val = Number(r.ot_surgeon_amount ?? 0);
        const amountColor = val === 0 ? '#c92a2a' : (val > 1 ? '#2f9e44' : '#000');
        const showBadge = filters.doctorId && doctorMatchesFilter(doctorName) && val > 0;

        return (
          <div>
            <div style={{fontWeight:600}}>{doctorName || '—'}</div>
            <div style={{color: amountColor, fontWeight: val === 0 || val > 1 ? 700 : 400}}>₹{val.toFixed(2)}</div>
            {showBadge && getPaymentStatusBadge(r, 'surgeon')}
          </div>
        );
      }
    },

    // Asst. OT Doctor with payment status
    {
      name: 'Asst. OT Doctor',
      width: '200px',
      cell: (r) => {
        let doctorName = r.ot_asst_doctor || r.asst_surgeon || '';
        if (doctorName && !isNaN(doctorName)) doctorName = getDoctorNameById(doctorName) || doctorName;
        const val = Number(r.ot_asst_amount ?? 0);
        const amountColor = val === 0 ? '#c92a2a' : (val > 1 ? '#2f9e44' : '#000');
        const showBadge = filters.doctorId && doctorMatchesFilter(doctorName) && val > 0;

        return (
          <div>
            <div style={{fontWeight:600}}>{doctorName || '—'}</div>
            <div style={{color: amountColor, fontWeight: val === 0 || val > 1 ? 700 : 400}}>₹{val.toFixed(2)}</div>
            {showBadge && getPaymentStatusBadge(r, 'asst')}
          </div>
        );
      }
    },

    // Anesthesia Doctor with payment status
    {
      name: 'Anesthesia Doctor',
      width: '200px',
      cell: (r) => {
        let doctorName = r.ot_anesthesia_doctor || r.anesthesia_doctor || '';
        if (doctorName && !isNaN(doctorName)) doctorName = getDoctorNameById(doctorName) || doctorName;
        const val = Number(r.ot_anesthesia_amount ?? 0);
        const amountColor = val === 0 ? '#c92a2a' : (val > 1 ? '#2f9e44' : '#000');
        const showBadge = filters.doctorId && doctorMatchesFilter(doctorName) && val > 0;

        return (
          <div>
            <div style={{fontWeight:600}}>{doctorName || '—'}</div>
            <div style={{color: amountColor, fontWeight: val === 0 || val > 1 ? 700 : 400}}>₹{val.toFixed(2)}</div>
            {showBadge && getPaymentStatusBadge(r, 'anesthesia')}
          </div>
        );
      }
    },

    // Pediatric Doctor with payment status
    {
      name: 'Pediatric Doctor',
      width: '200px',
      cell: (r) => {
        let doctorName = r.ot_pediatric_doctor || r.pediatric_doctor || '';
        if (doctorName && !isNaN(doctorName)) doctorName = getDoctorNameById(doctorName) || doctorName;
        const val = Number(r.ot_pediatric_amount ?? 0);
        const amountColor = val === 0 ? '#c92a2a' : (val > 1 ? '#2f9e44' : '#000');
        const showBadge = filters.doctorId && doctorMatchesFilter(doctorName) && val > 0;

        return (
          <div>
            <div style={{fontWeight:600}}>{doctorName || '—'}</div>
            <div style={{color: amountColor, fontWeight: val === 0 || val > 1 ? 700 : 400}}>₹{val.toFixed(2)}</div>
            {showBadge && getPaymentStatusBadge(r, 'pediatric')}
          </div>
        );
      }
    },

    // Total Amount
    {
      name: 'Total Amount',
      width: '120px',
      cell: r => {
        const val = Number(r.ot_total_amount ?? 0);
        const amountColor = val === 0 ? '#c92a2a' : (val > 1 ? '#2f9e44' : '#000');
        return <strong style={{color: amountColor, fontWeight: 700}}>₹{val.toFixed(2)}</strong>;
      }
    },

    { name: 'IPD Bill', selector: r => Number(r.ipd_bill_paid ?? r.ipd_bill_total_final ?? r.ipd_bill_total_paid ?? 0).toFixed(2), right: true, width: '100px' },
    { name: 'Paid (Cash)', selector: r => Number(r.ipd_paid_cash ?? 0).toFixed(2), right: true, width: '100px' },
    { name: 'Paid (Card)', selector: r => Number(r.ipd_paid_card ?? 0).toFixed(2), right: true, width: '100px' },
    { name: 'Paid (Online)', selector: r => Number(r.ipd_paid_online ?? 0).toFixed(2), right: true, width: '100px' },
    { name: 'Due', selector: r => Number(r.ipd_bill_total_due ?? r.ipd_due ?? r.due ?? 0).toFixed(2), right: true, width: '100px' },
    { name: 'Status', selector: r => r.status, width: '100px', cell: r => (r.status == 1 ? <span className="badge bg-success">Discharged</span> : <span className="badge bg-secondary">Admitted</span>) },
    { name: 'User', selector: r => r.last_used || r.user_display || r.fname || '', width: '120px' }
  ];

  const columns = [viewButtonColumn, ...dischargedColumns];

  const customStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color:'#405189', fontSize:'14px', fontWeight:700 } },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '8px', paddingRight:'8px' } }
  };

  const handleExportPdf = async () => {
    // ... (keeping existing PDF export code)
    try {
      const rows = filteredRecords;
      if (!rows || rows.length === 0) {
        Swal.fire('Nothing to export', 'No records available to export.', 'info');
        return;
      }

      const doc = new jsPDF('landscape', 'pt', 'a4');
      const title = `IPD Discharged Report`;
      const subtitle = `Date: ${getTodayYMD()}   Doctor: ${filters.doctorSearch || 'All'}`;

      doc.setFontSize(14);
      doc.text(title, 40, 40);
      doc.setFontSize(10);
      doc.text(subtitle, 40, 60);

      doc.setFontSize(9);
      doc.text(
        `Total Patients: ${totals.totalPatients}   Total IPD Bill: ₹${totals.totalIpdBill}   Paid (Cash): ₹${totals.paidCash}`,
        40, 78
      );

      const head = [['SL','IPD No','UHID','Patient','Discharge','Surgeon','Asst','Anes','Pedia','Total','IPD Bill','Due']];

      const body = rows.map((r, idx) => [
        String(idx + 1),
        `000${r.ipd_id ?? ''}`,
        (r.ipd_remarks === "Moved from OPD" ? r.opd_uhid_move : r.ipd_uhid) || '',
        (r.ipd_patient_name || '').replace(/\s+/g,' '),
        formatDateTime12(r.ipd_discharge_date || r.discharge_datetime || ''),
        Number(r.ot_surgeon_amount ?? 0).toFixed(2),
        Number(r.ot_asst_amount ?? 0).toFixed(2),
        Number(r.ot_anesthesia_amount ?? 0).toFixed(2),
        Number(r.ot_pediatric_amount ?? 0).toFixed(2),
        Number(r.ot_total_amount ?? 0).toFixed(2),
        Number(r.ipd_bill_total_final ?? 0).toFixed(2),
        Number(r.ipd_bill_total_due ?? 0).toFixed(2)
      ]);

      autoTable(doc, {
        startY: 95,
        head: head,
        body: body,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [241,245,251], textColor: 60 },
        margin: { left: 40, right: 20 }
      });

      doc.save(`ipd_discharged_${getTodayYMD()}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
      Swal.fire('Export failed', 'Could not generate PDF.', 'error');
    }
  };

  const paymentPreview = isDoctorPayModalOpen ? getPaymentPreview() : [];

  return (
    <div className="page-content">
      <div className="container-fluid">

        <ul className="nav nav-pills nav-customs nav-primary mb-3" role="tablist">
          <li className="nav-item">
            <a
              className={`nav-link ${activeTab === "discharged" ? "active" : ""}`}
              onClick={() => setActiveTab("discharged")}
              href="#"
            >
              IPD Discharge Patient
            </a>
          </li>
        </ul>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2">
              <div className="col-md-3">
                <label>Search</label>
                <input name="search" value={filters.search} onChange={handleFilterChange} className="form-control" placeholder="UHID / Name / Mobile"/>
              </div>

              <div className="col-md-2">
                <label>Discharge From</label>
                <input type="date" name="dischargeFrom" value={filters.dischargeFrom} onChange={handleFilterChange} className="form-control" />
              </div>

              <div className="col-md-1">
                <label>Time</label>
                <input type="time" name="dischargeFromTime" value={filters.dischargeFromTime} onChange={handleFilterChange} className="form-control" />
                <small className="text-muted d-block text-center">{formatTime12(filters.dischargeFromTime)}</small>
              </div>

              <div className="col-md-2">
                <label>Discharge To</label>
                <input type="date" name="dischargeTo" value={filters.dischargeTo} onChange={handleFilterChange} className="form-control" />
              </div>

              <div className="col-md-1">
                <label>Time</label>
                <input type="time" name="dischargeToTime" value={filters.dischargeToTime} onChange={handleFilterChange} className="form-control" />
                <small className="text-muted d-block text-center">{formatTime12(filters.dischargeToTime)}</small>
              </div>

              <div className="col-md-3">
                <label>Filter by Doctor</label>
                <Select
                  isClearable
                  value={filters.doctorId ? { value: String(filters.doctorId), label: filters.doctorSearch || String(filters.doctorId) } : null}
                  onChange={(sel) => {
                    if (!sel) {
                      setFilters(prev => ({ ...prev, doctorId: "", doctorSearch: "" }));
                      setPaymentStatuses({});
                    } else {
                      setFilters(prev => ({ ...prev, doctorId: String(sel.value), doctorSearch: String(sel.label) }));
                    }
                  }}
                  options={docsFromServer.map(d => ({ value: String(d.user_id), label: d.fname || d.name || d.username || String(d.user_id) }))}
                  placeholder="Select doctor..."
                />
                <small className="text-muted">Filters by Referred/OT/Anesthesia/Pediatric/Asst Doctor</small>
              </div>

            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">

            <div className="d-flex justify-content-end mb-2">
              <button className="btn btn-outline-primary" onClick={handleExportPdf}>Export PDF</button>
            </div>

            <DataTable
              columns={columns}
              data={filteredRecords}
              customStyles={customStyles}
              pagination
              highlightOnHover
              persistTableHead
              responsive
              onRowClicked={openPatientCard}
            />

            <div className="text-end mt-3 text-muted">
              <strong>Total IPD Bill:</strong> ₹{totals.totalIpdBill} &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>💵 <strong>Paid (Cash):</strong> ₹{totals.paidCash}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>💳 <strong>Paid (Card):</strong> ₹{totals.paidCard}</span> &nbsp;|&nbsp;
              <span style={{marginLeft:8}}>🌐 <strong>Paid (Online):</strong> ₹{totals.paidOnline}</span> &nbsp;|&nbsp;
              <strong>Total Due:</strong> ₹{totals.totalDue} &nbsp;|&nbsp;
              <strong>Total Patients:</strong> {totals.totalPatients}

              {/* Doctor Total with PAY button */}
              {filters.doctorId && (
                <div style={{marginTop:10, padding: '12px', background: '#f8f9fa', borderRadius: '8px'}}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong style={{fontSize: '16px'}}>Doctor: {filters.doctorSearch || filters.doctorId}</strong>
                      <div style={{marginTop: 4}}>
                        <span className="me-3">Total: <strong style={{color: '#2f9e44', fontSize: '18px'}}>₹{totals.doctorRoleTotal ?? '0.00'}</strong></span>
                        <small className="text-muted me-2">Surgeon: ₹{totals.doctorSurgeon ?? '0.00'}</small>
                        <small className="text-muted me-2">Asst: ₹{totals.doctorAsst ?? '0.00'}</small>
                        <small className="text-muted me-2">Anes: ₹{totals.doctorAnes ?? '0.00'}</small>
                        <small className="text-muted">Pedia: ₹{totals.doctorPedia ?? '0.00'}</small>
                      </div>
                    </div>
                    <button
                      className="btn btn-success btn-lg"
                      onClick={openDoctorPayModal}
                      style={{minWidth: '120px'}}
                    >
                      💰 PAY
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

      </div>

      {/* Doctor Payment Modal */}
      {isDoctorPayModalOpen && (
        <div className="modal fade show" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  💰 Pay Doctor: {doctorPayData.doctorName}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeDoctorPayModal}></button>
              </div>
              <div className="modal-body">
                
                {/* Summary */}
                <div className="alert alert-info">
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Total Due Amount:</strong>
                      <h3 className="mb-0">₹{doctorPayData.totalAmount.toFixed(2)}</h3>
                    </div>
                    <div className="col-md-4">
                      <strong>Patients:</strong>
                      <h3 className="mb-0">{doctorPayData.patients.length}</h3>
                    </div>
                    <div className="col-md-4">
                      <strong>Doctor:</strong>
                      <h5 className="mb-0">{doctorPayData.doctorName}</h5>
                    </div>
                  </div>
                </div>

                {/* Patient List with Preview */}
                <div className="table-responsive" style={{maxHeight: '400px', overflowY: 'auto'}}>
                  <table className="table table-sm table-bordered">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>#</th>
                        <th>Patient</th>
                        <th>UHID</th>
                        <th>Role</th>
                        <th className="text-end">Amount</th>
                        <th className="text-end">Already Paid</th>
                        <th className="text-end">Due</th>
                        <th className="text-end">Will Pay</th>
                        <th>New Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentPreview.map((p, i) => (
                        <tr key={i} className={p.newStatus === 'paid' ? 'table-success' : (p.newStatus === 'partial' ? 'table-warning' : '')}>
                          <td>{i + 1}</td>
                          <td>{p.patient_name}</td>
                          <td>{p.uhid}</td>
                          <td>
                            <span className={`badge ${
                              p.role === 'surgeon' ? 'bg-primary' :
                              p.role === 'asst' ? 'bg-info' :
                              p.role === 'anesthesia' ? 'bg-secondary' :
                              'bg-warning text-dark'
                            }`}>
                              {p.role_label}
                            </span>
                          </td>
                          <td className="text-end">₹{p.role_amount.toFixed(2)}</td>
                          <td className="text-end">₹{p.paid.toFixed(2)}</td>
                          <td className="text-end">₹{p.due.toFixed(2)}</td>
                          <td className="text-end">
                            <strong style={{color: p.willPay > 0 ? '#2f9e44' : '#aaa'}}>
                              ₹{p.willPay.toFixed(2)}
                            </strong>
                          </td>
                          <td>
                            {p.newStatus === 'paid' && <span className="badge bg-success">Paid</span>}
                            {p.newStatus === 'partial' && <span className="badge bg-warning text-dark">Partial (₹{p.newDue.toFixed(2)} due)</span>}
                            {p.newStatus === 'not_paid' && <span className="badge bg-danger">Not Paid</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Payment Input */}
                <div className="row mt-4 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label"><strong>Payment Amount</strong></label>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      value={doctorPayData.payAmount}
                      onChange={(e) => setDoctorPayData(prev => ({ ...prev, payAmount: Number(e.target.value) }))}
                      min="0"
                      max={doctorPayData.totalAmount}
                    />
                    <small className="text-muted">Max: ₹{doctorPayData.totalAmount.toFixed(2)}</small>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label"><strong>Payment Method</strong></label>
                    <select
                      className="form-select form-select-lg"
                      value={doctorPayData.paymentMethod}
                      onChange={(e) => setDoctorPayData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>
                  <div className="col-md-5 text-end">
                    <button
                      className="btn btn-secondary btn-lg me-2"
                      onClick={closeDoctorPayModal}
                      disabled={doctorPayData.loading}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-success btn-lg"
                      onClick={submitDoctorPayment}
                      disabled={doctorPayData.loading || doctorPayData.payAmount <= 0}
                    >
                      {doctorPayData.loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Processing...
                        </>
                      ) : (
                        <>💰 Pay ₹{Number(doctorPayData.payAmount || 0).toFixed(2)}</>
                      )}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}